import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Loader2, Paperclip, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { api } from '../../lib/client.ts';
import { openDocument, uploadTaskAttachment } from '../../lib/documents.ts';
import { Button } from '../ui/button.tsx';

/** `1.2 MB`, `48 KB`, `512 B`. */
function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

/**
 * A task row's paperclip and count (TS-1741); nothing when the task has no files, or when an
 * older API sends no count at all (the web can deploy before the API).
 */
export function AttachmentCount({ count }: { count: number | undefined }) {
  if (!count) return null;
  const label = `${count} attachment${count === 1 ? '' : 's'}`;
  return (
    <span
      className="text-muted-foreground inline-flex shrink-0 items-center gap-0.5 text-xs"
      title={label}
      aria-label={label}
      data-testid="attachment-count"
    >
      <Paperclip className="size-3" aria-hidden />
      {count}
    </span>
  );
}

/**
 * A task's files (TS-1741), in the task detail dialog: list and open for anyone who can see the
 * task; attach and remove for whoever may edit it (`canEdit`, the API's rule). The query lives
 * under `['tasks', …]` and every write refreshes `['tasks']`, so the attachment count on the
 * record sidebar, My Tasks and Team Tasks moves with it.
 */
export function TaskAttachments({ taskId, canEdit }: { taskId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(0);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const files = useQuery({
    queryKey: ['tasks', 'attachments', taskId],
    queryFn: () => api.tasks.attachments(taskId),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['tasks'] });
  const remove = useMutation({
    mutationFn: (documentId: string) => api.tasks.removeAttachment(taskId, documentId),
    onSuccess: async () => {
      setConfirming(null);
      await refresh();
    },
    onError: (err) => setError(describeError(err)),
  });

  /** One file after another: each is its own init → PUT → complete, and its own failure. */
  const upload = async (picked: File[]) => {
    setError(null);
    setUploading(picked.length);
    const failed: string[] = [];
    for (const file of picked) {
      try {
        await uploadTaskAttachment(taskId, file);
      } catch (err) {
        failed.push(`${file.name}: ${describeError(err)}`);
      }
      setUploading((n) => n - 1);
    }
    await refresh();
    if (failed.length > 0) setError(failed.join('\n'));
  };

  return (
    <section className="flex flex-col gap-1.5" data-testid="task-attachments">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium">Attachments</h4>
        {canEdit && (
          <>
            <input
              ref={fileInput}
              type="file"
              multiple
              className="hidden"
              aria-label="Attach files"
              onChange={(e) => {
                const picked = [...(e.target.files ?? [])];
                e.target.value = '';
                if (picked.length > 0) void upload(picked);
              }}
            />
            <Button
              variant="ghost"
              size="sm"
              disabled={uploading > 0}
              onClick={() => fileInput.current?.click()}
              data-testid="attach-file"
            >
              {uploading > 0 ? <Loader2 className="animate-spin" /> : <Paperclip />}
              {uploading > 0 ? `Uploading ${uploading}…` : 'Attach'}
            </Button>
          </>
        )}
      </div>
      {files.isPending ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : files.error ? (
        <p className="text-danger text-sm" role="alert">
          {describeError(files.error)}
        </p>
      ) : files.data.length === 0 ? (
        <p className="text-muted-foreground text-sm">No attachments.</p>
      ) : (
        <ul className="flex max-h-48 flex-col gap-1.5 overflow-y-auto">
          {files.data.map((d) => (
            <li
              key={d.id}
              className="border-border bg-card flex items-center gap-2 rounded-xl border px-2.5 py-1.5 text-sm"
              data-testid="attachment-row"
            >
              <FileText className="text-muted-foreground size-4 shrink-0" aria-hidden />
              <button
                type="button"
                className="text-brand min-w-0 truncate text-left hover:underline"
                title={d.filename}
                onClick={() =>
                  void openDocument(d.id).catch((err: unknown) => setError(describeError(err)))
                }
              >
                {d.filename}
              </button>
              <span className="text-muted-foreground ml-auto shrink-0 text-xs">
                {formatSize(d.sizeBytes)}
              </span>
              {canEdit &&
                (confirming === d.id ? (
                  <span className="flex shrink-0 items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setConfirming(null)}>
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(d.id)}
                      data-testid="confirm-remove-attachment"
                    >
                      Remove
                    </Button>
                  </span>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0"
                    aria-label={`Remove ${d.filename}`}
                    onClick={() => setConfirming(d.id)}
                    data-testid="remove-attachment"
                  >
                    <Trash2 />
                  </Button>
                ))}
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p className="text-danger text-sm whitespace-pre-line" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
